export function buildSuggestURL(settings, baseURL) {
  const byType = settings.searchBy === 'product_type';
  const query = (byType ? settings.typeTag : settings.vendor)?.trim();
  if (!query) return null;
  const gender = settings.genderTag?.trim();

  const url = new URL(settings.url, baseURL);
  url.searchParams.set('q', [gender, query].filter(Boolean).join(' '));
  url.searchParams.set('section_id', settings.sectionId);
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('resources[limit]', '10');
  url.searchParams.set('resources[options][unavailable_products]', 'hide');
  url.searchParams.set('resources[options][fields]', byType ? 'tag' : gender ? 'tag,vendor' : 'vendor');
  return url;
}

export function filterSuggestionItems(items, settings) {
  const normalize = (value) => (value || '').trim().toLowerCase();
  const byType = settings.searchBy === 'product_type';
  const query = normalize(byType ? settings.typeTag : settings.vendor);
  if (!query) return [];
  const gender = normalize(settings.genderTag);

  return items.filter(({ dataset }) => {
    if (!dataset.suggestProductId || dataset.suggestProductId === settings.productId) return false;
    if (dataset.suggestAvailable !== 'true') return false;
    const tags = JSON.parse(dataset.suggestTags || '[]').map(normalize);
    if (gender && !tags.includes(gender)) return false;
    if (!byType) return normalize(dataset.suggestVendor) === query;
    return tags.includes(query);
  }).slice(0, 10);
}

class ProductRecommendationsSuggest extends HTMLElement {
  #controller;
  #loaded = false;

  connectedCallback() {
    if (!this.#loaded) this.#load();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  async #load() {
    const url = buildSuggestURL(this.dataset, window.location.href);
    if (!url) return;

    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);
      const html = await response.text();
      if (controller.signal.aborted || !this.isConnected) return;

      const document = new DOMParser().parseFromString(html, 'text/html');
      const results = document.getElementById(this.id)?.querySelector('[data-suggest-results]');
      if (!results) throw new Error('Suggested products section was not returned');

      let productCount = 0;
      for (const list of results.querySelectorAll('.resource-list')) {
        const items = [...list.querySelectorAll('[data-suggest-product-id]')];
        const matches = filterSuggestionItems(items, this.dataset).map((item) => item.cloneNode(true));
        productCount = Math.max(productCount, matches.length);

        items.forEach((item, index) => {
          if (matches[index]) {
            item.replaceWith(matches[index]);
          } else {
            const slide = item.closest('slideshow-slide.resource-list__slide');
            const editorialItem = item.parentElement?.parentElement?.matches('.editorial-product__grid')
              ? item.parentElement : null;
            (slide || editorialItem || item).remove();
          }
        });

        const slideshow = list.querySelector('slideshow-component.resource-list__carousel--custom');
        slideshow?.style.setProperty('--slideshow-timeline', matches.map((_, index) => `--slide-${index}`).join(','));
      }

      this.#loaded = true;
      if (!productCount) {
        this.hidden = true;
        return;
      }

      const stylesheet = document.querySelector('style[data-section-stylesheet]');
      if (stylesheet) results.prepend(stylesheet);
      this.querySelector('[data-suggest-results]').replaceChildren(...results.childNodes);
      this.hidden = false;
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.hidden = true;
      console.error('Suggested products error:', error);
    } finally {
      if (this.#controller === controller) this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('product-recommendations-suggest')) {
  customElements.define('product-recommendations-suggest', ProductRecommendationsSuggest);
}
